/**
 * 名字 → 角色 - 主入口
 * 状态机驱动的单页应用
 */

import "./style.css";
import { createScreen0, type Screen0Controller } from "./screens/screen0";
import { createScreen1, type Screen1Controller } from "./screens/screen1";
import {
  createScreen2,
  pickRandomCombos,
  type Screen2Combo,
  type Screen2Controller,
} from "./screens/screen2";
import { createScreen3, type Screen3Controller } from "./screens/screen3";
import { createScreen4, type Screen4Controller } from "./screens/screen4";
import { createScreen5, type Screen5Controller } from "./screens/screen5";
import { saveLastGeneration } from "./lastGen";
import { getBgm } from "./bgm";
import type { CharAnalysis, MonsterDesc } from "../shared/types";

const FONT_FAMILY = "Vonwaon, monospace";

interface GridState {
  lastName: string;
  analysis: CharAnalysis;
  monsters: MonsterDesc[];
  combos: Screen2Combo[];
}

type Screen =
  | { name: "0"; ctrl: Screen0Controller }
  | { name: "1"; ctrl: Screen1Controller; onRetry: () => void; onCancel: () => void }
  | { name: "2"; ctrl: Screen2Controller } & GridState
  | { name: "3"; ctrl: Screen3Controller } & GridState & { selectedIndex: number }
  | { name: "4"; ctrl: Screen4Controller }
  | { name: "5"; ctrl: Screen5Controller };

let current: Screen | null = null;
let appRoot: HTMLElement;

function switchTo(next: Screen): void {
  if (current) {
    current.ctrl.destroy();
  }
  current = next;
}

function createBgmButton(): HTMLElement {
  const btn = document.createElement("button");
  btn.className = "bgm-btn";
  btn.title = "背景音乐（诡谲欢快 chiptune）";
  btn.innerHTML = `
    <svg class="icon-muted" viewBox="0 0 16 16" fill="currentColor">
      <rect x="2" y="6" width="2" height="4"/>
      <rect x="4" y="4" width="2" height="8"/>
      <rect x="6" y="2" width="2" height="12"/>
      <rect x="8" y="3" width="1" height="1"/><rect x="9" y="4" width="1" height="1"/><rect x="10" y="5" width="1" height="1"/><rect x="11" y="6" width="1" height="1"/><rect x="12" y="7" width="1" height="1"/><rect x="13" y="8" width="1" height="1"/><rect x="14" y="9" width="1" height="1"/>
      <rect x="14" y="3" width="1" height="1"/><rect x="13" y="4" width="1" height="1"/><rect x="12" y="5" width="1" height="1"/><rect x="11" y="6" width="1" height="1"/><rect x="10" y="7" width="1" height="1"/><rect x="9" y="8" width="1" height="1"/><rect x="8" y="9" width="1" height="1"/>
    </svg>
    <svg class="icon-playing" viewBox="0 0 16 16" fill="currentColor">
      <rect x="2" y="6" width="2" height="4" fill="rgba(180, 90, 140, 0.95)"/>
      <rect x="4" y="4" width="2" height="8" fill="rgba(180, 90, 140, 0.95)"/>
      <rect x="6" y="2" width="2" height="12" fill="rgba(180, 90, 140, 0.95)"/>
      <rect x="9" y="6" width="1" height="4" fill="rgba(255, 60, 120, 0.95)"/>
      <rect x="11" y="4" width="1" height="8" fill="rgba(255, 60, 120, 0.95)"/>
      <rect x="13" y="2" width="1" height="12" fill="rgba(255, 60, 120, 0.95)"/>
    </svg>
  `;
  btn.addEventListener("click", () => {
    const bgm = getBgm();
    const playing = bgm.toggle();
    btn.classList.toggle("playing", playing);
  });
  return btn;
}

function startApp(): void {
  appRoot = document.getElementById("app")!;

  // 持久化 BGM 按钮（不受屏切换影响）
  const bgmBtn = createBgmButton();
  appRoot.appendChild(bgmBtn);

  const goToScreen4 = () => {
    switchTo({ name: "4", ctrl: createScreen4({
      root: appRoot,
      onBack: () => goToScreen0(),
    }) });
  };

  const goToScreen5 = () => {
    switchTo({ name: "5", ctrl: createScreen5({
      root: appRoot,
      onBack: () => goToScreen0(),
    }) });
  };

  const goToScreen0 = () => {
    switchTo({ name: "0", ctrl: createScreen0({
      root: appRoot,
      fontFamily: FONT_FAMILY,
      onMonsters: (name, analysis, monsters) => {
        // 屏 1 显示期间，先把当前 4 怪存为"最近生成"（供系统对战用）
        const combos = pickRandomCombos(monsters.length);
        saveLastGeneration(monsters, combos);
        switchTo({
          name: "1",
          ctrl: createScreen1({
            root: appRoot,
            onRetry: () => goToScreen0(),
            onCancel: () => goToScreen0(),
          }),
          onRetry: () => goToScreen0(),
          onCancel: () => goToScreen0(),
        });
        setTimeout(() => {
          if (current?.name !== "1") return;
          goToScreen2(name, analysis, monsters, combos);
        }, 1200);
      },
      onError: (msg) => {
        switchTo({
          name: "1",
          ctrl: createScreen1({
            root: appRoot,
            onRetry: () => goToScreen0(),
            onCancel: () => goToScreen0(),
          }),
          onRetry: () => goToScreen0(),
          onCancel: () => goToScreen0(),
        });
        if (current?.name === "1") {
          current.ctrl.showError(msg);
        }
      },
      onGoZoo: () => goToScreen4(),
      onGoBattle: () => goToScreen5(),
    }) });
  };

  const goToScreen2 = (name: string, analysis: CharAnalysis, monsters: MonsterDesc[], combos?: Screen2Combo[]) => {
    const finalCombos = combos ?? pickRandomCombos(monsters.length);
    switchTo({
      name: "2",
      ctrl: createScreen2({
        root: appRoot,
        fontFamily: FONT_FAMILY,
        analysis,
        monsters,
        combos: finalCombos,
        onSelect: (i) => goToScreen3(name, analysis, monsters, finalCombos, i),
        onRegenerate: () => goToScreen0(),
        onBack: () => goToScreen0(),
        onGoZoo: () => goToScreen4(),
      }),
      lastName: name,
      analysis,
      monsters,
      combos: finalCombos,
    });
  };

  const goToScreen3 = (
    name: string,
    analysis: CharAnalysis,
    monsters: MonsterDesc[],
    combos: Screen2Combo[],
    selectedIndex: number
  ) => {
    const combo = combos[selectedIndex];
    switchTo({
      name: "3",
      ctrl: createScreen3({
        root: appRoot,
        analysis,
        monster: monsters[selectedIndex],
        combo,
        onBack: () => goToScreen2(name, analysis, monsters),
        onRegenerate: () => goToScreen0(),
        onGoZoo: () => goToScreen4(),
      }),
      lastName: name,
      analysis,
      monsters,
      combos,
      selectedIndex,
    });
  };

  goToScreen0();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", startApp);
} else {
  startApp();
}
